import { type MouseEvent } from 'react';

// UXV.V3 (#1418) — click-to-comment overlay over the governance screen preview.
//
// SECURITY (C1): this overlay is rendered by the React PARENT and positioned absolutely over the
// sandboxed iframe — it is NEVER injected into the iframe. The iframe keeps `sandbox=""` (no
// allow-scripts/allow-same-origin). Clicks are captured here, in the parent tree, by the capture
// layer's onClick; there is NO postMessage and NO cross-frame communication (Q5/C2 not exercised).
// The parent container must be `position:relative` (added in ScreenPreviewCard around ONLY the
// iframe block) so `inset:0` aligns the overlay to the iframe exactly.

export interface Annotation {
  id: string;
  /** Pin position as a fraction (0..1) of the rendered iframe — authoritative. */
  xPct: number;
  yPct: number;
  /** Server-derived best-effort section name; null when the spec has no sections. */
  componentRegion: string | null;
  comment: string;
  /** ISO-8601. */
  createdAt: string;
  author: string;
}

interface Props {
  /** When false, the capture layer passes pointer events through (iframe scrollable/inspectable). */
  active: boolean;
  annotations: Annotation[];
  /** Called with the click position as [0,1] fractions of the overlay (== iframe) box. */
  onPlace: (xPct: number, yPct: number) => void;
}

export default function AnnotationOverlay({ active, annotations, onPlace }: Props) {
  function handleClick(e: MouseEvent<HTMLDivElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    // Guard a zero-size box BEFORE dividing: (clientX - left) / 0 → Infinity/NaN, and Math.min/max
    // do NOT sanitise NaN (Math.max(NaN, 0) === NaN). Happens in happy-dom (no layout) and on a
    // not-yet-laid-out frame. No box → no pin.
    if (rect.width === 0 || rect.height === 0) return;
    const xPct = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
    const yPct = Math.min(Math.max((e.clientY - rect.top) / rect.height, 0), 1);
    onPlace(xPct, yPct);
  }

  return (
    <>
      {/* Capture layer — pointer-events ONLY when annotation mode is on. */}
      <div
        data-testid="annotation-capture"
        onClick={active ? handleClick : undefined}
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: active ? 'auto' : 'none',
          cursor: active ? 'crosshair' : 'default',
          background: active ? 'rgba(124,58,237,0.04)' : 'transparent',
        }}
      />
      {/* Pins — always visible (even when mode is off) so reviewers see existing annotations while
          reading. Numbered to cross-reference the AnnotationList. Never block pointer events. */}
      {annotations.map((a, i) => (
        <div
          key={a.id}
          data-testid="annotation-pin"
          title={a.comment}
          style={{
            position: 'absolute',
            left: `${a.xPct * 100}%`,
            top: `${a.yPct * 100}%`,
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
            width: 22,
            height: 22,
            borderRadius: '50%',
            background: 'var(--primary, #7c3aed)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 700,
            border: '2px solid #fff',
            boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
          }}
        >
          {i + 1}
        </div>
      ))}
    </>
  );
}
